import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircleIcon, ChevronDownIcon, BookOpenIcon } from 'lucide-react';
interface Tomo {
  title: string;
  emoji: string;
  items: string[];
}
function Accordion({
  tomo,
  index
}: {
  tomo: Tomo;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay: 0.08 * index
  }} className="border border-yellow-400/30 rounded-xl overflow-hidden bg-white/5" data-id="element-10">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors" data-id="element-11">
        <div className="flex items-center gap-3" data-id="element-12">
          <span className="text-2xl" data-id="element-13">{tomo.emoji}</span>
          <span className="text-lg md:text-xl font-bold text-white" data-id="element-14">
            {tomo.title}
          </span>
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-15">
          <ChevronDownIcon className="w-6 h-6 text-yellow-400" data-id="element-16" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-17">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-18">
            <div className="px-5 pb-5 pt-2 border-t border-yellow-400/10" data-id="element-19">
              <ul className="space-y-2" data-id="element-20">
                {tomo.items.map((item, i) => <li key={i} className={`text-gray-300 ${item.startsWith(' ') ? 'ml-6 text-gray-400 text-sm' : ''}`} data-id="element-21">
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function AudienceSection() {
  const problems = ['Pasás horas organizando cada sesión desde cero', 'No sabés cómo estructurar evaluaciones y protocolos correctamente', 'Te cuesta organizar el seguimiento de tus pacientes', 'Muchas veces improvisás ejercicios y tratamientos en el momento', 'Sentís que te faltan herramientas profesionales para ser más eficiente'];
  const tomos: Tomo[] = [{
    title: 'TOMO 1 — Evaluación Inteligente',
    emoji: '📘',
    items: ['🔹 1. Introducción a la evaluación eficiente', '🔹 2. Sistema de evaluación en 5 pasos', '🔹 3. Evaluación por regiones (enfoque práctico)', '   Columna cervical', '   Columna lumbar', '   Hombro', '   Rodilla', '   Tobillo/pie', '🔹 4. Detección rápida de patrones', '🔹 5. Errores comunes en evaluación', '🔹 6. Checklist de evaluación rápida (lista para usar)']
  }, {
    title: 'TOMO 2 — Protocolos Base',
    emoji: '📘',
    items: ['🔹 1. Qué es un protocolo eficiente', '🔹 2. Estructura universal de sesión', '🔹 3. Protocolos por objetivo', '   Dolor', '   Movilidad', '   Activación', '   Control motor', '🔹 4. Protocolos por región', '🔹 5. Progresiones y regresiones', '🔹 6. Protocolos express (20–30 min)']
  }, {
    title: 'TOMO 3 — Ejercicios Clave',
    emoji: '📘',
    items: ['🔹 1. Cómo elegir ejercicios sin perder tiempo', '🔹 2. Ejercicios por objetivo', '🔹 3. Ejercicios por región', '🔹 4. Ejercicios funcionales', '🔹 5. Cómo explicar ejercicios al paciente', '🔹 6. Adaptaciones rápidas']
  }, {
    title: 'TOMO 4 — Plantillas Profesionales',
    emoji: '📘',
    items: ['🔹 1. Ficha inicial de paciente', '🔹 2. Hoja de evolución', '🔹 3. Plan de tratamiento', '🔹 4. Seguimiento del paciente', '🔹 5. Plantillas imprimibles']
  }, {
    title: 'TOMO 5 — Casos Prácticos',
    emoji: '📘',
    items: ['🔹 1. Cómo abordar un caso clínico', '🔹 2. Caso: dolor cervical por estudio', '🔹 3. Caso: lumbalgia mecánica', '🔹 4. Caso: dolor de hombro', '🔹 5. Caso: dolor de rodilla', '🔹 6. Caso: sobrecarga en gemelos (running)', '🔹 7. Errores en casos reales']
  }, {
    title: 'TOMO 6 — Planillas y Fichas de Alto Rendimiento',
    emoji: '📘',
    items: ['🔹 1. Sistema de registro rápido de pacientes', '   Ficha resumida de atención diaria', '   Registro express por sesión', '🔹 2. Planilla de organización semanal', '   Agenda de pacientes', '   Distribución de carga laboral', '   Control de sesiones', '🔹 3. Planilla de seguimiento clínico', '   Evolución simplificada', '   Indicadores de progreso', '   Alertas de estancamiento', '🔹 4. Ficha rápida de ejercicios para pacientes', '   Entrega post sesión', '   Instrucciones simples', '🔹 5. Planilla de progresiones automáticas', '   Escalas de avance', '   Niveles de dificultad', '   Decisión rápida', '🔹 6. Checklist de sesión eficiente', '   Antes de la sesión', '   Durante', '   Después', '🔹 7. Sistema de carga laboral optimizado', '   Cómo reducir tiempo administrativo']
  }];
  return <section className="w-full px-4 py-20 bg-gradient-to-b from-[#1e293b] to-[#0f172a]" data-id="element-22">
      <div className="max-w-4xl mx-auto" data-id="element-23">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-24">
          <h2 className="text-3xl md:text-5xl font-black mb-12 text-center leading-tight" data-id="element-25">
            ¿Sos kinesiólogo y te pasa alguna de estas situaciones?
          </h2>

          <div className="space-y-4 mb-16" data-id="element-26">
            {problems.map((problem, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.08 * index
          }} className="flex items-start gap-3 bg-white/5 border border-white/10 p-4 rounded-lg" data-id="element-27">
                <XCircleIcon className="w-6 h-6 text-[#ef4444] flex-shrink-0 mt-0.5" data-id="element-28" />
                <span className="text-lg text-gray-300" data-id="element-29">{problem}</span>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-30">
            <h3 className="text-2xl md:text-4xl font-black mb-8 text-center text-yellow-400" data-id="element-31">
              Mirá todo lo que incluye el MegaPack:
            </h3>

            <div className="space-y-3 mb-12" data-id="element-32">
              {tomos.map((tomo, index) => <Accordion key={index} tomo={tomo} index={index} data-id="element-33" />)}
            </div>

            <p className="text-xl md:text-2xl font-bold text-center text-gray-300 mb-10" data-id="element-34">
              MegaPack KineEficiente fue creado para resolver exactamente estos
              problemas.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}